import type { StripePrice } from './types';

/** How to behave when the user's currency has no price at the wanted interval. */
export type MissingCurrencyPolicy =
  /** Show nothing. Correct where the alternative is quoting a price we would not charge. */
  | 'none'
  /** Fall back to USD — what `POST /v1/checkout/session` would actually charge. */
  | 'usd';

const DEFAULT_CURRENCY = 'usd';

/**
 * THE single author of "which of a product's prices do we show this user".
 *
 * `GET /v1/products` returns **every** active price for the product — both
 * currencies, every interval — and reports the country-detected currency
 * separately as `detectedCurrency`. Picking one is therefore the caller's job,
 * and getting it wrong is silent: a price is still rendered, just not the one
 * that applies.
 *
 * `SubscriptionManagePage` got it wrong by taking `prices[0]` under a comment
 * claiming "monthly if available", with no filter for currency or interval.
 * Stripe lists prices newest-created first — and this product's USD and GBP
 * prices were created in the same second — so which figure a UK user saw was
 * effectively a coin flip (#1135). `PricingPage` had the correct rule inline;
 * this is that rule, in one place, so a third page cannot invent a fourth
 * answer.
 *
 * THE FALLBACK IS THE CALLER'S DECISION, not a default, because the two callers
 * genuinely differ:
 *
 *  - The pricing page must not advertise a price in a currency the visitor is
 *    not being quoted, so it shows no tier at all (`'none'`, its behaviour
 *    before this helper existed).
 *  - The management page describes a subscription that already exists. If the
 *    user's currency has no price, USD is what `POST /v1/checkout/session`
 *    charges — it has that fallback — so USD is the honest answer there, and
 *    'N/A' on a plan someone is actively paying for is worse than a correct
 *    figure in the currency they were actually billed in (`'usd'`).
 *
 * Neither branch is exercised in dev, whose catalogue I checked: two active
 * prices, USD and GBP, both monthly. I did not check the staging or production
 * Stripe accounts, so this is a statement about dev and not about "every
 * environment". It is written down because the day it stops holding anywhere,
 * the two pages should still disagree deliberately.
 *
 * INTERVAL IS ASSUMED, AND THAT IS A LIMIT WORTH KNOWING. Callers ask for one —
 * `month` by default — because nothing on the client knows which interval a
 * given subscriber is on: the entitlements API carries `expiresAt` and
 * `willRenew` but no price or interval. Today that assumption is always right,
 * because `POST /v1/checkout/session` only ever selects `interval === 'month'`
 * and the product carries no other price. Add an annual price and this stops
 * being an assumption and starts being a wrong answer, confidently rendered —
 * at which point the fix is not a better guess here, it is reading the real
 * figure from `GET /v1/billing/status`.
 */
export function selectPriceForCurrency(
  prices: StripePrice[] | undefined,
  detectedCurrency: string | undefined,
  options: { interval?: string; ifMissing?: MissingCurrencyPolicy } = {}
): StripePrice | undefined {
  const { interval = 'month', ifMissing = 'none' } = options;
  if (!prices?.length) return undefined;

  // Lower-cased on both sides. Stripe returns currency codes lower-case and
  // `detectedCurrency` comes from a lower-case map, so this is defensive rather
  // than load-bearing — but a strict compare that silently yields "no price" is
  // the exact failure this helper exists to stop, and `formatPrice` normalises
  // already, so being inconsistent about it would itself be the bug.
  const wanted = (detectedCurrency || DEFAULT_CURRENCY).toLowerCase();
  const at = (currency: string) =>
    prices.find(
      p => p.currency.toLowerCase() === currency && p.interval === interval
    );

  return at(wanted) ?? (ifMissing === 'usd' ? at(DEFAULT_CURRENCY) : undefined);
}

/**
 * Format a price for display, e.g. `$2.99/month`.
 *
 * Amounts are in the currency's minor unit, as Stripe returns them. That holds
 * for every currency this product is sold in and every currency the checkout
 * route can select, but it is NOT universal — Stripe sends zero-decimal
 * currencies such as JPY unscaled, so dividing those by 100 would under-report
 * the price a hundredfold. Supporting one means teaching this function the
 * currency's exponent, not just adding a symbol.
 */
export function formatPrice(price: StripePrice): string {
  const symbols: Record<string, string> = { usd: '$', gbp: '£' };
  const symbol =
    symbols[price.currency.toLowerCase()] ?? `${price.currency.toUpperCase()} `;
  return `${symbol}${(price.amount / 100).toFixed(2)}/${price.interval}`;
}
